import { quarantineCustomerOwnedSessionStorage } from "@/app/booking/core/customerOwnedSessionStorageBarrier";
import { createVersionedSessionDraftStore } from "@/app/booking/core/versionedSessionDraftStore";
import {
  validatePackagePurchaseAttempt,
  type PackagePurchaseAttemptV1,
} from "@/app/commerce/packages/packagePurchaseAttempt";
import { inspectPackagePurchaseAttemptPersisted } from "@/app/commerce/packages/packagePurchaseAttemptStore";

import {
  SPECIALIST_LOCATION_GATEWAY_ACTIVATION_REVISION,
} from "./activation";
import { createSpecialistLocationGatewayOwnerKey } from "./canonicalKey";
import {
  validateSpecialistLocationGatewayNavigationAuthority,
  type SpecialistLocationGatewayCommittedNavigationLineageV1,
} from "./contract";
import { createSpecialistLocationGatewayPackageDestinationFingerprint } from "./destinationTransactions";
import {
  beginSpecialistLocationGatewayTerminalWrite,
  completeSpecialistLocationGatewayTerminalWrite,
  specialistLocationGatewayTerminalWriteIsCurrent,
} from "./terminalWriteCoordinator";

export const SPECIALIST_LOCATION_GATEWAY_PACKAGE_ATTEMPT_ORIGIN_SCHEMA_VERSION =
  1 as const;
export const SPECIALIST_LOCATION_GATEWAY_PACKAGE_ATTEMPT_ORIGIN_STORAGE_KEY =
  "volera:specialist-location-gateway-terminal-attempt-origins:package-purchase:v1";
export const SPECIALIST_LOCATION_GATEWAY_PACKAGE_ATTEMPT_ORIGIN_MAX_AGE_MS =
  24 * 60 * 60 * 1_000 + 10 * 60 * 1_000;

const PACKAGE_ATTEMPT_FINGERPRINT_PREFIX =
  "specialist-location-gateway-package-attempt-v1-";

export interface SpecialistLocationGatewayPackageAttemptOriginV1 {
  schemaVersion: typeof SPECIALIST_LOCATION_GATEWAY_PACKAGE_ATTEMPT_ORIGIN_SCHEMA_VERSION;
  kind: "specialist-location-gateway-package-attempt-origin";
  draftId: string;
  ownerKey: string;
  activationRevision: typeof SPECIALIST_LOCATION_GATEWAY_ACTIVATION_REVISION;
  attemptFingerprint: string;
  customerAccountId: string;
  destinationKind: "package";
  destinationDraftId: string;
  destinationDraftFingerprint: string;
  idempotencyKey: string;
  checkoutActivatedAtEpochMs: number;
  lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
}

export type ReadSpecialistLocationGatewayPackageAttemptOriginResult =
  | {
      status: "ready";
      origin: SpecialistLocationGatewayPackageAttemptOriginV1;
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
    }
  | { status: "absent" | "owner-mismatch" | "unreadable" };

export interface AuthorizeSpecialistLocationGatewayPackageAttemptInput {
  attempt: unknown;
  originRequirement: "gateway" | "legacy-non-gateway";
  /** Only the synchronous newly-created `starting` path may publish a marker. */
  allowOriginCreation: boolean;
  /** A fresh, action-time lineage is mandatory for creation and retryable work. */
  freshLineage:
    | SpecialistLocationGatewayCommittedNavigationLineageV1
    | null;
  isCurrentOwner: () => boolean;
}

export type InvokeSpecialistLocationGatewayPackageAttemptResult<Result> =
  | {
      status: "invoked";
      authority: "gateway" | "legacy-non-gateway";
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1 | null;
      providerInvocation:
        | { status: "returned"; value: Result }
        | { status: "threw" };
    }
  | { status: "refused" };

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, expected: readonly string[]) {
  const actual = Object.keys(value).sort();
  const canonical = [...expected].sort();
  return (
    actual.length === canonical.length &&
    actual.every((key, index) => key === canonical[index])
  );
}

function isIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= 512 &&
    !/[\u0000-\u001f\u007f]/u.test(value)
  );
}

function isOwnerKey(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.startsWith("specialist-location-gateway-owner-v1:") &&
    value.length <= 20_000
  );
}

function isEpoch(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= 8_640_000_000_000_000
  );
}

function hashSerializableSnapshot(value: string, seed: number) {
  let hash = seed >>> 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(hash ^ value.charCodeAt(index), 16_777_619);
  }
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x7feb352d);
  hash ^= hash >>> 15;
  hash = Math.imul(hash, 0x846ca68b);
  hash ^= hash >>> 16;
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function createPackageAttemptFingerprint(attempt: PackagePurchaseAttemptV1) {
  try {
    const serialized = JSON.stringify([
      attempt.draftId,
      attempt.customerAccountId,
      attempt.draft,
      attempt.checkoutContext,
      attempt.checkoutActivatedAtEpochMs,
      attempt.paymentMethodId,
      attempt.idempotencyKey,
    ]);
    const digest = [
      0x811c9dc5,
      0x9e3779b9,
      0x85ebca6b,
      0xc2b2ae35,
      0x27d4eb2f,
      0x165667b1,
      0xd3a2646c,
      0xfd7046c5,
    ]
      .map((seed) => hashSerializableSnapshot(serialized, seed))
      .join("");
    return `${PACKAGE_ATTEMPT_FINGERPRINT_PREFIX}${digest}`;
  } catch {
    return null;
  }
}

function isPackageAttemptFingerprint(value: unknown): value is string {
  return (
    typeof value === "string" &&
    /^specialist-location-gateway-package-attempt-v1-[a-f0-9]{64}$/u.test(
      value,
    )
  );
}

function cloneCommittedLineage(value: unknown) {
  try {
    const validation =
      validateSpecialistLocationGatewayNavigationAuthority(value);
    return validation.valid &&
      validation.draft.kind ===
        "specialist-location-gateway-navigation-lineage"
      ? validation.draft
      : null;
  } catch {
    return null;
  }
}

function createOrigin(input: {
  attempt: PackagePurchaseAttemptV1;
  lineage: unknown;
}): SpecialistLocationGatewayPackageAttemptOriginV1 | null {
  const lineage = cloneCommittedLineage(input.lineage);
  const destinationDraftFingerprint =
    createSpecialistLocationGatewayPackageDestinationFingerprint(
      input.attempt.draft,
    );
  const attemptFingerprint = createPackageAttemptFingerprint(input.attempt);

  if (
    !lineage ||
    !destinationDraftFingerprint ||
    !attemptFingerprint ||
    input.attempt.draft.entryPoint.kind !== "specialist-packages" ||
    lineage.customerAccountId !== input.attempt.customerAccountId ||
    lineage.destinationKind !== "package" ||
    lineage.destinationDraftId !== input.attempt.draftId ||
    lineage.destinationDraftFingerprint !== destinationDraftFingerprint ||
    lineage.selection.selectedLocationId !== input.attempt.draft.locationId ||
    lineage.selection.selectedUserLocationId !==
      input.attempt.draft.userLocationId
  ) {
    return null;
  }

  const ownerKey = createSpecialistLocationGatewayOwnerKey({
    stage: "package-attempt-origin-v1",
    fields: [
      input.attempt.draftId,
      attemptFingerprint,
      input.attempt.customerAccountId,
      destinationDraftFingerprint,
      input.attempt.idempotencyKey,
      input.attempt.checkoutActivatedAtEpochMs,
      lineage.ownerKey,
      SPECIALIST_LOCATION_GATEWAY_ACTIVATION_REVISION,
    ],
  });
  const candidate: SpecialistLocationGatewayPackageAttemptOriginV1 = {
    schemaVersion:
      SPECIALIST_LOCATION_GATEWAY_PACKAGE_ATTEMPT_ORIGIN_SCHEMA_VERSION,
    kind: "specialist-location-gateway-package-attempt-origin",
    draftId: input.attempt.draftId,
    ownerKey,
    activationRevision: SPECIALIST_LOCATION_GATEWAY_ACTIVATION_REVISION,
    attemptFingerprint,
    customerAccountId: input.attempt.customerAccountId,
    destinationKind: "package",
    destinationDraftId: input.attempt.draftId,
    destinationDraftFingerprint,
    idempotencyKey: input.attempt.idempotencyKey,
    checkoutActivatedAtEpochMs: input.attempt.checkoutActivatedAtEpochMs,
    lineage,
  };
  const validation = validateOrigin(candidate);
  return validation.valid ? validation.draft : null;
}

function validateOrigin(
  value: unknown,
):
  | {
      valid: true;
      draft: SpecialistLocationGatewayPackageAttemptOriginV1;
    }
  | {
      valid: false;
      reason: "invalid-specialist-location-gateway-package-attempt-origin";
    } {
  const invalid = () =>
    ({
      valid: false as const,
      reason:
        "invalid-specialist-location-gateway-package-attempt-origin" as const,
    });
  try {
    if (
      !isRecord(value) ||
      !hasExactKeys(value, [
        "schemaVersion",
        "kind",
        "draftId",
        "ownerKey",
        "activationRevision",
        "attemptFingerprint",
        "customerAccountId",
        "destinationKind",
        "destinationDraftId",
        "destinationDraftFingerprint",
        "idempotencyKey",
        "checkoutActivatedAtEpochMs",
        "lineage",
      ]) ||
      value.schemaVersion !==
        SPECIALIST_LOCATION_GATEWAY_PACKAGE_ATTEMPT_ORIGIN_SCHEMA_VERSION ||
      value.kind !== "specialist-location-gateway-package-attempt-origin" ||
      !isIdentifier(value.draftId) ||
      !isOwnerKey(value.ownerKey) ||
      value.activationRevision !==
        SPECIALIST_LOCATION_GATEWAY_ACTIVATION_REVISION ||
      !isPackageAttemptFingerprint(value.attemptFingerprint) ||
      !isIdentifier(value.customerAccountId) ||
      value.destinationKind !== "package" ||
      value.destinationDraftId !== value.draftId ||
      typeof value.destinationDraftFingerprint !== "string" ||
      !isIdentifier(value.idempotencyKey) ||
      !isEpoch(value.checkoutActivatedAtEpochMs)
    ) {
      return invalid();
    }
    const lineage = cloneCommittedLineage(value.lineage);
    if (
      !lineage ||
      lineage.customerAccountId !== value.customerAccountId ||
      lineage.destinationKind !== "package" ||
      lineage.destinationDraftId !== value.destinationDraftId ||
      lineage.destinationDraftFingerprint !==
        value.destinationDraftFingerprint
    ) {
      return invalid();
    }
    const expectedOwnerKey = createSpecialistLocationGatewayOwnerKey({
      stage: "package-attempt-origin-v1",
      fields: [
        value.draftId,
        value.attemptFingerprint,
        value.customerAccountId,
        value.destinationDraftFingerprint,
        value.idempotencyKey,
        value.checkoutActivatedAtEpochMs,
        lineage.ownerKey,
        value.activationRevision,
      ],
    });
    if (expectedOwnerKey !== value.ownerKey) return invalid();
    return {
      valid: true,
      draft: {
        schemaVersion:
          SPECIALIST_LOCATION_GATEWAY_PACKAGE_ATTEMPT_ORIGIN_SCHEMA_VERSION,
        kind: "specialist-location-gateway-package-attempt-origin",
        draftId: value.draftId,
        ownerKey: value.ownerKey,
        activationRevision: value.activationRevision,
        attemptFingerprint: value.attemptFingerprint,
        customerAccountId: value.customerAccountId,
        destinationKind: "package",
        destinationDraftId: value.destinationDraftId,
        destinationDraftFingerprint: value.destinationDraftFingerprint,
        idempotencyKey: value.idempotencyKey,
        checkoutActivatedAtEpochMs: value.checkoutActivatedAtEpochMs,
        lineage,
      },
    };
  } catch {
    return invalid();
  }
}

const packageAttemptOriginStore =
  createVersionedSessionDraftStore<SpecialistLocationGatewayPackageAttemptOriginV1>(
    {
      storageKey:
        SPECIALIST_LOCATION_GATEWAY_PACKAGE_ATTEMPT_ORIGIN_STORAGE_KEY,
      storeKind: "specialist-location-gateway-package-attempt-origins",
      maxEntries: 8,
      maxAgeMs:
        SPECIALIST_LOCATION_GATEWAY_PACKAGE_ATTEMPT_ORIGIN_MAX_AGE_MS,
      maxSerializedCharacters: 1_500_000,
      validate: validateOrigin,
    },
  );

function sameSnapshot(left: unknown, right: unknown) {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

function ambiguousStatus(status: string) {
  return (
    status === "unreadable" ||
    status === "persistence-failed" ||
    status === "readback-mismatch"
  );
}

function inspectPersistedAttemptExact(attempt: PackagePurchaseAttemptV1) {
  try {
    const inspected = inspectPackagePurchaseAttemptPersisted(attempt.draftId);
    if (inspected.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
      return false;
    }
    if (
      inspected.status !== "present" ||
      !sameSnapshot(inspected.draft, attempt)
    ) {
      return false;
    }
    return true;
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return false;
  }
}

function inspectOrigin(draftId: string) {
  try {
    const inspected = packageAttemptOriginStore.inspectPersistedDraft(draftId);
    if (inspected.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
    }
    return inspected;
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unreadable" as const };
  }
}

function exactOriginForAttempt(
  attempt: PackagePurchaseAttemptV1,
  candidate: SpecialistLocationGatewayPackageAttemptOriginV1,
) {
  const expected = createOrigin({ attempt, lineage: candidate.lineage });
  return expected && sameSnapshot(expected, candidate) ? expected : null;
}

export function readSpecialistLocationGatewayPackageAttemptOriginStrict(input: {
  attempt: unknown;
  expectedCustomerAccountId: string;
}): ReadSpecialistLocationGatewayPackageAttemptOriginResult {
  let attemptValue: unknown;
  let expectedCustomerAccountId: string;
  let validation: ReturnType<typeof validatePackagePurchaseAttempt>;
  try {
    attemptValue = input.attempt;
    expectedCustomerAccountId = input.expectedCustomerAccountId;
    validation = validatePackagePurchaseAttempt(attemptValue);
  } catch {
    return { status: "owner-mismatch" };
  }
  if (
    !validation.valid ||
    validation.attempt.customerAccountId !== expectedCustomerAccountId ||
    validation.attempt.draft.entryPoint.kind !== "specialist-packages"
  ) {
    return { status: "owner-mismatch" };
  }
  if (!inspectPersistedAttemptExact(validation.attempt)) {
    return { status: "unreadable" };
  }
  const inspected = inspectOrigin(validation.attempt.draftId);
  if (inspected.status !== "present") return { status: inspected.status };
  const exact = exactOriginForAttempt(validation.attempt, inspected.draft);
  if (!exact) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "owner-mismatch" };
  }
  return { status: "ready", origin: exact, lineage: exact.lineage };
}

export function invokeSpecialistLocationGatewayPackageAttemptProvider<Result>(
  input: AuthorizeSpecialistLocationGatewayPackageAttemptInput & {
    invokeProvider: () => Result;
  },
): InvokeSpecialistLocationGatewayPackageAttemptResult<Result> {
  let invokeProvider: () => Result;
  let attemptValue: unknown;
  let originRequirement: "gateway" | "legacy-non-gateway";
  let allowOriginCreation: boolean;
  let freshLineage: SpecialistLocationGatewayCommittedNavigationLineageV1 | null;
  let isCurrentOwner: () => boolean;
  try {
    invokeProvider = input.invokeProvider;
    attemptValue = input.attempt;
    originRequirement = input.originRequirement;
    allowOriginCreation = input.allowOriginCreation;
    freshLineage = input.freshLineage;
    isCurrentOwner = input.isCurrentOwner;
  } catch {
    return { status: "refused" };
  }
  if (
    typeof invokeProvider !== "function" ||
    typeof isCurrentOwner !== "function" ||
    typeof allowOriginCreation !== "boolean" ||
    (originRequirement !== "gateway" &&
      originRequirement !== "legacy-non-gateway")
  ) {
    return { status: "refused" };
  }
  let validation: ReturnType<typeof validatePackagePurchaseAttempt>;
  try {
    validation = validatePackagePurchaseAttempt(attemptValue);
  } catch {
    return { status: "refused" };
  }
  if (!validation.valid) return { status: "refused" };
  const attempt = validation.attempt;
  if (
    attempt.state.kind !== "starting" &&
    attempt.state.kind !== "retryable" &&
    attempt.state.kind !== "unknown"
  ) {
    return { status: "refused" };
  }
  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify(["package-attempt-origin", attempt.draftId]),
    isCurrentOwner,
  );
  if (!lease) return { status: "refused" };
  try {
    if (!inspectPersistedAttemptExact(attempt)) {
      return { status: "refused" };
    }
    if (originRequirement === "legacy-non-gateway") {
      if (!inspectPersistedAttemptExact(attempt)) {
        return { status: "refused" };
      }
      // This owner bracket is the final operation before provider entry.
      if (!specialistLocationGatewayTerminalWriteIsCurrent(
        lease,
        isCurrentOwner,
      )) {
        return { status: "refused" };
      }
      try {
        return {
          status: "invoked",
          authority: "legacy-non-gateway",
          lineage: null,
          providerInvocation: {
            status: "returned",
            value: invokeProvider(),
          },
        };
      } catch {
        return {
          status: "invoked",
          authority: "legacy-non-gateway",
          lineage: null,
          providerInvocation: { status: "threw" },
        };
      }
    }
    if (attempt.draft.entryPoint.kind !== "specialist-packages") {
      return { status: "refused" };
    }

    const freshOrigin = freshLineage
      ? createOrigin({ attempt, lineage: freshLineage })
      : null;
    let inspected = inspectOrigin(attempt.draftId);
    let origin: SpecialistLocationGatewayPackageAttemptOriginV1 | null = null;
    let created = false;

    if (inspected.status === "present") {
      origin = exactOriginForAttempt(attempt, inspected.draft);
      if (!origin) {
        quarantineCustomerOwnedSessionStorage();
        return { status: "refused" };
      }
    } else if (inspected.status === "absent") {
      if (
        !allowOriginCreation ||
        attempt.state.kind !== "starting" ||
        !freshOrigin
      ) {
        return { status: "refused" };
      }
      let creation: ReturnType<
        typeof packageAttemptOriginStore.createDraftStrictPersisted
      >;
      try {
        creation =
          packageAttemptOriginStore.createDraftStrictPersisted(freshOrigin);
      } catch {
        creation = { status: "unreadable" };
      }
      if (creation.status === "created") {
        created = true;
        origin = creation.draft;
      } else if (creation.status === "conflict") {
        inspected = inspectOrigin(attempt.draftId);
        origin =
          inspected.status === "present"
            ? exactOriginForAttempt(attempt, inspected.draft)
            : null;
        if (!origin) quarantineCustomerOwnedSessionStorage();
      } else {
        const deterministicallySupersededByAbsence =
          creation.status === "readback-mismatch" &&
          inspectOrigin(attempt.draftId).status === "absent";
        if (
          !deterministicallySupersededByAbsence &&
          ambiguousStatus(creation.status)
        ) {
          quarantineCustomerOwnedSessionStorage();
        }
        return { status: "refused" };
      }
    } else {
      return { status: "refused" };
    }

    const compensateCreatedOrigin = () => {
      if (!created || !origin) return true;
      try {
        const removed =
          packageAttemptOriginStore.removeDraftStrictPersisted(origin);
        const deterministicallySupersededByAbsence =
          removed.status === "readback-mismatch" &&
          inspectOrigin(origin.draftId).status === "absent";
        const compensated =
          removed.status === "removed" ||
          removed.status === "absent" ||
          deterministicallySupersededByAbsence;
        if (!compensated) quarantineCustomerOwnedSessionStorage();
        return compensated;
      } catch {
        quarantineCustomerOwnedSessionStorage();
        return false;
      }
    };

    if (!origin) return { status: "refused" };
    if (
      attempt.state.kind === "retryable" &&
      (!freshOrigin || !sameSnapshot(freshOrigin, origin))
    ) {
      compensateCreatedOrigin();
      return { status: "refused" };
    }
    if (
      allowOriginCreation &&
      (!freshOrigin || !sameSnapshot(freshOrigin, origin))
    ) {
      compensateCreatedOrigin();
      return { status: "refused" };
    }
    // The provider callback is the only caller-owned code after these exact
    // read-backs and final owner bracket. It starts synchronously while the
    // lease is still current; auth or plain owner drift cannot pass the
    // lease→owner→lease bracket with cached bytes from the former owner.
    const finalAttemptIsExact = inspectPersistedAttemptExact(attempt);
    const finalOrigin = inspectOrigin(attempt.draftId);
    const finalSnapshotsAreExact =
      finalAttemptIsExact &&
      finalOrigin.status === "present" &&
      sameSnapshot(finalOrigin.draft, origin);
    if (
      !finalSnapshotsAreExact ||
      !specialistLocationGatewayTerminalWriteIsCurrent(
        lease,
        isCurrentOwner,
      )
    ) {
      compensateCreatedOrigin();
      return { status: "refused" };
    }
    try {
      return {
        status: "invoked",
        authority: "gateway",
        lineage: origin.lineage,
        providerInvocation: {
          status: "returned",
          value: invokeProvider(),
        },
      };
    } catch {
      return {
        status: "invoked",
        authority: "gateway",
        lineage: origin.lineage,
        providerInvocation: { status: "threw" },
      };
    }
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}

export function clearSpecialistLocationGatewayPackageAttemptOrigins() {
  try {
    return packageAttemptOriginStore.clearDrafts();
  } catch {
    return false;
  }
}
